
import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { uniqBy } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { debounceTime, filter, takeUntil, tap } from "rxjs/operators";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import {
    DestineringsraderUpdatedEvent
} from "~app/features/transport/transportplanera/components/tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-events";
import {
    BegransaVirkesorderraderModalComponent
} from "~app/features/transport/transportplanera/components/tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-modal.component";
import { RuttChangedEvent } from "~app/features/transport/transportplanera/components/tpl-transportavstand-modal/tpl-transportavstand-eventhandler";
import { IArbetsorderDetail } from "../planera-detail/planera-detail-models";
import { ArbetsorderMonitor, ArbetsorderMonitorService } from "../services/arbetsorder-monitor.service";
import { ArbetsorderService } from "../services/arbetsorder.service";

interface IArbetsorderViewModel {
    antalRader: number;
    arbetsorderId: number;
    direktutbyteStatus: string[];
    isDestineringsalternativJusterad: boolean;
}

@Component({
    selector: "rask-arbetsorder-alert",
    templateUrl: "./arbetsorder-alert.component.html",
    styleUrls: ["./arbetsorder-alert.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MessagePanelComponent,
        AsyncPipe
    ]
})
export class ArbetsorderAlertComponent implements OnInit, OnChanges, OnDestroy {
    alerts$ = new BehaviorSubject<IMessageModel[]>([]);
    @Input() arbetsorder: IArbetsorderDetail;

    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();

    private arbetsorderViewModel: IArbetsorderViewModel;
    private monitor: ArbetsorderMonitor;

    handleMessagePanelClick(message: IMessageModel): void {
        if (message.anchor === "#begransa-rader") {
            BegransaVirkesorderraderModalComponent.open(this.arbetsorder.id, "standard", this.modalService);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const arbetsorderChanges = changes.arbetsorder;
        if (!arbetsorderChanges) {
            return;
        }

        if (arbetsorderChanges.currentValue.id === arbetsorderChanges.previousValue?.id) {
            return;
        }

        if (!arbetsorderChanges.isFirstChange()) {
            this.destroy();
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    ngOnInit(): void {
        this.init();
    }

    private addAncorInformation(messageList: IMessageModel[]) {
        if (this.arbetsorder.viol3) {
            return;
        }
        messageList.forEach(x => {
            if (x.severity === MessageSeverity.Update) {
                x.anchor = "#visa-events";
                x.anchorText = "Visa händelser";
            }
        }
        );
    }

    private destroy(): void {
        this.monitor.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private hanteraDirektutbyteStatusFactory(): IMessageModel[] {
        return this.arbetsorderViewModel?.direktutbyteStatus.map(x => ({
            message: x,
            severity: MessageSeverity.Info,
            anchor: null,
            anchorText: null
        })) ?? [];
    }

    private hanteraRaderMessageFactory(): IMessageModel[] {
        if (this.arbetsorder.viol3) {
            return [];
        }

        const antalRader = this.arbetsorderViewModel.antalRader;
        const isDestineringsalternativJusterad = this.arbetsorderViewModel.isDestineringsalternativJusterad;

        if (antalRader === 0 && !isDestineringsalternativJusterad) {
            return [];
        }

        let message: string;
        const isSent = this.arbetsorder.arbetsorderValsSynkroniseringstatus != null;

        if ((isSent && antalRader > 60) || antalRader > 50) {
            const text = isSent ? "uppdaterar" : "skickar";
            message = `Arbetsordern har ${antalRader} rader. Du behöver begränsa antalet innan du ${text} virkesorder.`;
        } else if (isDestineringsalternativJusterad || isSent && antalRader > 50) {
            message = "Antal rader på arbetsordern har justerats.";
        } else if (this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin)) {
            message = `Arbetsordern har ${antalRader} rader.`;
        }

        if (!message) {
            return [];
        }

        const messagemodel = {
            message: message,
            severity: MessageSeverity.Info,
            anchor: "#begransa-rader",
            anchorText: "Hantera rader"
        };
        return [messagemodel];
    }

    private init(): void {
        this.arbetsorderViewModel = {
            arbetsorderId: this.arbetsorder.id,
            antalRader: this.arbetsorder.antalRader,
            isDestineringsalternativJusterad: this.arbetsorder.isDestineringsalternativJusterad,
            direktutbyteStatus: []
        };

        this.arbetsorderService.antalVirkesorderRader$.next(this.arbetsorder.antalRader);

        this.monitor = this.arbetsorderMonitorService.connect();

        this.monitor.registerAlertMessageFactory(() => this.hanteraDirektutbyteStatusFactory());
        this.monitor.registerAlertMessageFactory(() => this.hanteraRaderMessageFactory());

        this.monitor.alerts$.pipe(
            tap(x => {
                // Arbetsorder-alert visar endast samma "message" en gång, även om exempelvis metadata kan skifta.
                // Om det finns ett "prettyFormat" så visar vi ÄVEN det men vi låter också originalet synas.

                const expanded = x.flatMap(msg => {
                    if (msg.metadata?.prettyFormat) {
                        return [
                            msg,
                            {
                                ...msg,
                                message: msg.metadata?.prettyFormat,
                                priority: 100
                            }
                        ];
                    }
                    return [msg];
                });

                const unique = uniqBy(expanded, m => m.message);

                this.addAncorInformation(unique);
                this.alerts$.next(unique);
            }),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(x => this.changeDetectorRef.detectChanges());

        const rebuilder$ = this.initRebuilder();
        rebuilder$.pipe(
            debounceTime(100),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(() => this.monitor.rebuildAlerts());

        this.monitor.getWork("viol2.spegla.started", () => this.arbetsorderService.refreshAntalVirkesorderrader(this.arbetsorder.id));

        this.arbetsorderService.refreshDirektutbyteStatus(this.arbetsorder.id);
    }

    private initRebuilder(): Subject<unknown> {
        const invalidate$ = new Subject<void>();

        this.eventService
            .subscribeEvent<DestineringsraderUpdatedEvent>(DestineringsraderUpdatedEvent.type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(tap(x => {
                this.arbetsorderViewModel.antalRader = x.antalRader;
                this.arbetsorderViewModel.isDestineringsalternativJusterad = x.inaktivaDestineringar.length > 0;
            }))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(() => invalidate$.next());

        this.eventService
            .subscribeEvent<RuttChangedEvent>(RuttChangedEvent.type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(() => invalidate$.next());

        this.arbetsorderService.antalVirkesorderRader$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(tap(x => this.arbetsorderViewModel.antalRader = x))
            .subscribe(() => invalidate$.next());

        this.arbetsorderService.direktutbyteStatus$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(tap(x => this.arbetsorderViewModel.direktutbyteStatus = x))
            .subscribe(() => invalidate$.next());

        return invalidate$;
    }
}
